import { cloneShapeStyle, Ease } from './helpers';
import { SHAPE_STYLES } from './constants';
import { addGameObject, createShapeStyleAnimation } from './entity';
import { ObjectType } from './shared/api_shared_types';
import { setVisibilityState } from './simulate';
import { AnimationType, VisibilityState, RenderItemType, Shape } from './types';
import type * as t from './types';
import logger from './controllers/logger';
import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageCircle,
  type RenderPackageClearRect,
  type RenderPackageItem,
  type RenderPackageRect,
} from './render_package';

export function controller(): t.GameObjectController {
  return {
    getNew: getZeroContainer,
    sync: null,
    update,
    createPackage,
  };
}

export function getZeroContainer(ent: t.Entity): t.Container {
  let zeroContainer: t.Container = {
    ...ent,
    shape: Shape.RECT,
    objectType: ObjectType.CONTAINER,
    defaultStyle: null,
    doClear: false,
    shapeStyle: null,
    highlightStyle: null,
    appearDuration: 0.2,
    disappearDuration: 0.2,
    cornerRadius: 0,
    cornerRadius1: 0,
    cornerRadius2: 0,
    cornerRadius3: 0,
    cornerRadius4: 0,
    highlightSpeed: 0.2,
    defaultSpeed: 0.2,
  };

  return zeroContainer;
}

export function createAndAddLocalContainer(
  manager: t.GameManager,
  ent: t.Entity,
  defaultStyle?: t.RenderShapeStyle,
  highlightStyle?: t.RenderShapeStyle,
): t.Container {
  const newContainer = getZeroContainer(ent);
  addGameObject(manager, newContainer);

  setVisibilityState(newContainer, VisibilityState.APPEARING);

  if (highlightStyle) {
    setContainerStyles(newContainer, defaultStyle, highlightStyle);
  } else {
    setContainerStyle(newContainer, defaultStyle);
  }

  return newContainer;
}

export function setContainerStyles(
  container: t.Container,
  defaultStyle: t.RenderShapeStyle,
  highlightStyle: t.RenderShapeStyle,
): void {
  container.defaultStyle = cloneShapeStyle(defaultStyle);
  container.highlightStyle = cloneShapeStyle(highlightStyle);
  container.shapeStyle = cloneShapeStyle(container.highlighted ? highlightStyle : defaultStyle);

  for (const anim of container.animations) {
    if (anim.type === AnimationType.SHAPE_STYLE) {
      anim.doKill = true;
    }
  }
}

export function setContainerStyle(container: t.Container, style: t.RenderShapeStyle | null): void {
  container.defaultStyle = style ? cloneShapeStyle(style) : null;
  container.highlightStyle = null;
  container.shapeStyle = style ? cloneShapeStyle(style) : null;

  for (const anim of container.animations) {
    if (anim.type === AnimationType.SHAPE_STYLE) {
      anim.doKill = true;
    }
  }
}

function update(c: t.Container, parentSizeChanged: boolean): boolean {
  if (c.highlightStyle) {
    if (c.startHighlightThisFrame) {
      createShapeStyleAnimation(c.manager, c, {
        type: AnimationType.SHAPE_STYLE,
        end: c.highlightStyle,
        duration: c.highlightSpeed,
        easeFunction: Ease.inOutQuad,
      });
    } else if (c.stopHighlightThisFrame) {
      createShapeStyleAnimation(c.manager, c, {
        type: AnimationType.SHAPE_STYLE,
        end: c.defaultStyle,
        duration: c.defaultSpeed,
        easeFunction: Ease.inOutQuad,
      });
    }
  }

  return false;
}

export function createPackage(
  container: t.Container,
  position: t.V2,
  scale: number,
  rotation: number = 0,
): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  if (!container.shapeStyle && !container.doClipChildren) return rPackage;

  if (container.shape === Shape.RECT) {
    let rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      rotation,
      positionX: position.x,
      positionY: position.y,
      width: container.width,
      height: container.height,
      opacity: container.opacity,

      shapeStyle: container.shapeStyle,
      cornerRadius1: container.cornerRadius || container.cornerRadius1,
      cornerRadius2: container.cornerRadius || container.cornerRadius2,
      cornerRadius3: container.cornerRadius || container.cornerRadius3,
      cornerRadius4: container.cornerRadius || container.cornerRadius4,
      shadow: container.shapeStyle ? container.shapeStyle.shadow : null,
    };

    rPackage.push(rect);
  } else if (container.shape === Shape.CIRCLE) {
    if (container.radius < 0) {
      return rPackage;
    }

    if (container.doClear) {
      let clearRect: RenderPackageClearRect = {
        type: RenderItemType.clearRect,
        ...EMPTY_RENDER_TRANSFORM,
        positionX: position.x - scale * container.radius * 0.999,
        positionY: position.y - scale * container.radius * 0.999,
        roundedCorners: true,
        scale,
        width: container.radius * 1.998,
        height: container.radius * 1.998,
        cornerRadius1: container.radius * 2,
        cornerRadius2: container.radius * 2,
        cornerRadius3: container.radius * 2,
        cornerRadius4: container.radius * 2,
      };
      rPackage.push(clearRect);
    }

    let circle: RenderPackageCircle = {
      type: RenderItemType.circle,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: position.x,
      positionY: position.y,
      radius: container.radius,
      shapeStyle: container.shapeStyle || SHAPE_STYLES.NONE,
      shadow: container.shapeStyle ? container.shapeStyle.shadow : null,
    };
    rPackage.push(circle);
  }

  return rPackage;
}
